import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { useHistory } from 'react-router-dom';
import { isPlatform } from '@ionic/react';
import { Intercom } from '@capacitor-community/intercom';
import { useEvent } from '@mindly/ui-components';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { setUserDeviceFCMToken } from '../../../api/notifications';
import { useAnalytics, useAuthFlow } from 'src/shared/hooks';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCanDismiss,
  setIsModalShowed,
  setIsOpenPushNotificationModal,
  setIsShowPushNotificationWarning,
  setPushNotificationStatus,
} from '../../../redux/pushNotifications/reducer';
import {
  selectCanDismiss,
  selectIsModalShowed,
  selectIsOpenPushNotificationModal,
  selectIsShowPushNotificationWarning,
  selectPushNotificationStatus,
} from 'src/redux/pushNotifications/selector';
import { OPEN_STREAM_CHAT_MODAL_ROUTE } from 'src/shared/types/stream-chat.type';
import { userSelector } from 'src/redux/user/selector';
import { useChat } from 'src/shared/hooks/useChat';
import { useRemoteConfig } from 'src/hooks/useRemoteConfig/useRemoteConfig';

type PermissionProviderProps = React.PropsWithChildren<any>;

export function PermissionProvider({ children }: PermissionProviderProps) {
  const history = useHistory();
  const { authUser } = useAuthFlow();
  const { handleChatClick } = useChat();

  const { isActiveFeature } = useRemoteConfig();

  const currentUser = useSelector(userSelector);
  const pushNotificationStatus = useSelector(selectPushNotificationStatus);

  const dispatch = useDispatch();
  const currentUserId = authUser?.uid;

  /**
   * Updates the status of push notifications.
   * @returns a Promise that resolves to the status of push notifications
   */
  const updatePermissions = useCallback(async () => {
    const status = await PushNotifications.checkPermissions();
    dispatch(setPushNotificationStatus(status.receive));
    return status as unknown as PermissionState;
  }, [setPushNotificationStatus]);

  useEffect(() => {
    if (!isPlatform('hybrid')) return;
    if (!currentUser?.id) return;

    (async function () {
      await updatePermissions();
    })();

    PushNotifications.addListener('registration', async (token) => {
      await Promise.all([
        updatePermissions(),
        setUserDeviceFCMToken(currentUser?.id, token.value),
        isPlatform('hybrid') && !isPlatform('ios') && Intercom.sendPushTokenToIntercom({ value: token.value }),
      ]);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', async (payload) => {
      const route = payload?.notification?.data?.route;
      const isStreamChatEnabled = await isActiveFeature('is_stream_chat_enabled');

      if (route === OPEN_STREAM_CHAT_MODAL_ROUTE && currentUser?.id && isStreamChatEnabled) {
        handleChatClick();
      } else if (route && route !== OPEN_STREAM_CHAT_MODAL_ROUTE) {
        history.push(route);
      }
    });

    PushNotifications.addListener('registrationError', (error: unknown) => {
      console.log('Error: ' + JSON.stringify(error, null, 2));
    });

    return () => {
      PushNotifications.removeAllListeners();
    };
  }, [currentUser?.id]);

  useEffect(() => {
    if (pushNotificationStatus === 'prompt') {
      setIsOpenPushNotificationModal(true);
    }
  }, [pushNotificationStatus]);

  useEffect(() => {
    if (currentUserId && isPlatform('android') && isPlatform('hybrid')) {
      (async () => {
        await PushNotifications.register();
      })();
    }
  }, [currentUserId]);

  return children;
}

/**
 * Hook for managing push notifications.
 * @returns an object with the following properties:
 * - canDismiss: boolean indicating whether the user can dismiss the push notification permission prompt
 * - pushNotificationStatus: string indicating the current status of push notifications ('denied', 'granted', or 'prompt')
 * - isOpenPushNotificationModal: boolean indicating whether the push notification permission modal is open
 * - allowPushNotificationHandler: a function that can be used to handle the user allowing or denying push notifications
 * - openAppNativeSettings: a function that can be used to open the native settings app for the app
 * - setIsOpenPushNotificationModal: a function that can be used to set whether the push notification permission modal is open
 * - setIsShowPushNotificationWarning: a function that can be used to set whether to show the push notification warning
 * - updatePermissions: a function that can be used to update the permissions for push notifications
 * - isShowPushNotificationWarning: boolean indicating whether to show the push notification warning
 */
export function usePushNotifications() {
  const [startInterval, setStartInterval] = React.useState<boolean>(false);
  const intervalIdRef = useRef<number>();
  const dispatch = useDispatch();
  const { trackPredefinedEvent } = useAnalytics();
  const canDismiss = useSelector(selectCanDismiss);
  const isOpenPushNotificationModal = useSelector(selectIsOpenPushNotificationModal);
  const isModalShowed = useSelector(selectIsModalShowed);
  const pushNotificationStatus = useSelector(selectPushNotificationStatus);
  const isShowPushNotificationWarning = useSelector(selectIsShowPushNotificationWarning);

  /**
   * Updates the status of push notifications.
   * @returns a Promise that resolves to the status of push notifications
   */
  const updatePermissions = useCallback(async () => {
    if (!isPlatform('hybrid')) return;

    const status = await PushNotifications.checkPermissions();
    dispatch(setPushNotificationStatus(status.receive));
    return status as unknown as PermissionState;
  }, [setPushNotificationStatus]);

  /**
   * Opens the native settings app for the app.
   * @returns a Promise that resolves when the settings app is opened
   */
  const openAppNativeSettings = useCallback(async () => {
    try {
      await NativeSettings.open({
        optionIOS: IOSSettings.App,
        optionAndroid: AndroidSettings.ApplicationDetails,
      });
      setStartInterval(true);
    } catch (error) {
      console.error('Setting open with Error: ', error);
    }
  }, [pushNotificationStatus, updatePermissions]);

  /**
   * Handles the user allowing or denying push notifications.
   * @returns a Promise that resolves when the user has made a decision
   */
  const allowPushNotificationHandler = useEvent(async () => {
    dispatch(setCanDismiss(true));

    let permStatus = await PushNotifications.checkPermissions();

    trackPredefinedEvent('NOTIFICATION_PERMISSION_CLICKED', {
      permission_type: permStatus.receive,
    });

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      dispatch(setIsShowPushNotificationWarning(true));
      dispatch(setIsOpenPushNotificationModal(false));
      dispatch(setIsModalShowed(true));
    }

    if (permStatus.receive === 'granted') {
      await PushNotifications.register();
      dispatch(setIsShowPushNotificationWarning(false));
      dispatch(setIsOpenPushNotificationModal(false));
      dispatch(setIsModalShowed(true));
    }
  });

  useEffect(() => {
    if (!startInterval) return;
    let retries = 0;
    let maxRetries = 5;

    // Check if push notifications are granted
    if (pushNotificationStatus === 'granted') {
      (async () => {
        await PushNotifications.register();
      })();
      clearInterval(intervalIdRef.current);
      setStartInterval(false);
      return;
    }

    // Check if maximum retries have been reached
    if (retries === maxRetries) {
      clearInterval(intervalIdRef.current);
      setStartInterval(false);
      return;
    }

    intervalIdRef.current = window.setInterval(async () => {
      retries++;
      // Update permissions and increment retries
      await updatePermissions();
    }, 6000);
  }, [startInterval, pushNotificationStatus, updatePermissions]);

  return useMemo(() => {
    return {
      canDismiss,
      pushNotificationStatus,
      isOpenPushNotificationModal:
        !isModalShowed &&
        isOpenPushNotificationModal &&
        pushNotificationStatus !== '' &&
        pushNotificationStatus !== 'denied' &&
        pushNotificationStatus !== 'granted',
      allowPushNotificationHandler,
      openAppNativeSettings,
      setIsOpenPushNotificationModal: (val: boolean) => dispatch(setIsOpenPushNotificationModal(val)),
      updatePermissions,
      isShowPushNotificationWarning:
        isShowPushNotificationWarning && pushNotificationStatus !== '' && pushNotificationStatus !== 'granted',
    };
  }, [
    canDismiss,
    pushNotificationStatus,
    isOpenPushNotificationModal,
    isShowPushNotificationWarning,
    allowPushNotificationHandler,
    openAppNativeSettings,
    setIsOpenPushNotificationModal,
    setIsShowPushNotificationWarning,
    updatePermissions,
  ]);
}
